import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, QueryList, ViewChildren } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  BucketSettings,
  Cadence,
  CollectionsEmailSettings,
  CollectionsSequence,
  CollectionsSettingsV2,
  CollectionsThrottleSettings,
  CollectionsTriggersSettings,
  EmailNotificationType,
  GraphQl,
  MessageSettings,
  SmartStatementActionDefinitionJson,
  SmartStatementAgingExpression,
  SmartStatementBucket,
  SmartStatementBucketSetting,
  TriggerRule,
  UpdateCollectionsSettingsPayload,
} from '@anduin/api';
import { FirmPortalPermission, LaunchDarklyFlag, LaunchDarklyService, LaunchDarklyState, PermissionsState } from '@anduin/auth';
import { CollectionsMode } from '@anduin/config';
import {
  DEFAULT_SNACKBAR_CONFIG,
  LoadableHelper,
  LoadableStatus,
  NestedKey,
  RecursivePartial,
  SnackbarComponent,
  SnackbarType,
  areArraysEqual,
  extractFailureContext,
  getFirstOfQueryList,
  getLoadedResults,
  hashObject,
  ignoreFalsy,
  ignoreNullish,
  isEmpty,
  isLoaded,
  mapToAndResult,
  replaceArrayItem,
} from '@anduin/shared';
import { Store } from '@ngxs/store';
import { cloneDeep, partition } from 'lodash';
import { BehaviorSubject, Observable, Subject, combineLatest, defer } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, shareReplay, skip, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import {
  AdditionalRecipientsInputComponent,
  CollectionsAdditionalRecipient,
  CollectionsAddressesComponent,
  CollectionsAutoApproveComponent,
  CollectionsBucketMessageFormComponent,
  CollectionsMessageFormComponent,
  CollectionsThrottlingComponent,
  SequenceTriggerFormComponent,
  ThrottlingState,
} from '../../components';
import { StatementRecipientLabel } from '../../models';
import {
  ClearCollectionsPageData,
  CommonCollectionsState,
  GetActiveCadences,
  GetCollectionsSettings,
  GetMessageSettings,
  GetMessageVariables,
  GetSequenceAggregatesByCadenceId,
  UpdateCadence,
  UpdateCollectionsSettings,
  UpdateMessageSettings,
  UpdateSequence,
} from '../../state';
import { CollectionsSettingsStep, CollectionsTrackService, SettingsChangedSection } from '../../tracking';

@Component({
  selector: 'app-collections-settings',
  templateUrl: './collections-settings.component.html',
  styleUrls: ['./collections-settings.component.scss'],
  standalone: false,
  providers: [CollectionsTrackService],
})
export class CollectionsSettingsComponent implements OnInit, AfterViewInit, OnDestroy {
  /**
   * Debounce window for `SETTINGS_THROTTLING_CHANGED`. The throttling input emits on every
   * keystroke as the user types in the day-count field; without a quiet window the funnel
   * fills with intermediate values (`days: 1`, `12`, `120`) that aren't the user's intent.
   */
  private static readonly THROTTLING_TRACK_DEBOUNCE_MS = 500;

  private readonly _unsubscribe$: Subject<void> = new Subject<void>();
  private readonly _throttlingTrack$ = new Subject<ThrottlingState>();
  private _pendingChangedSections: SettingsChangedSection[] = [];
  LoadableStatus = LoadableStatus;

  // Permission to edit settings - determines if form is editable or read-only
  canEditSettings$ = this.store.select(PermissionsState.hasPermission([FirmPortalPermission.COLLECTIONS_CAN_EDIT_COLLECTIONS_SETTINGS]));

  //#region Collections v1 (existing logic/data not suitable to preserve)

  // Forms
  @ViewChildren(CollectionsThrottlingComponent) throttlingForm: QueryList<CollectionsThrottlingComponent>;
  @ViewChildren(SequenceTriggerFormComponent) triggerForm: QueryList<SequenceTriggerFormComponent>;
  @ViewChildren(CollectionsAutoApproveComponent) autoApproveForm: QueryList<CollectionsAutoApproveComponent>;
  @ViewChildren(CollectionsAddressesComponent) addressesForm: QueryList<CollectionsAddressesComponent>;
  @ViewChildren(CollectionsMessageFormComponent) messageForm: QueryList<CollectionsMessageFormComponent>;
  @ViewChildren(CollectionsBucketMessageFormComponent) bucketMessageForm: QueryList<CollectionsBucketMessageFormComponent>;
  @ViewChildren(AdditionalRecipientsInputComponent) additionalRecipientsForm: QueryList<AdditionalRecipientsInputComponent>;

  updatedCadence$ = new BehaviorSubject<Cadence>(undefined);
  // Updates to the sequence will need to get serialized into the TriggerType for comparison
  updatedSequence$ = new BehaviorSubject<CollectionsSequence>(undefined);
  updatedMessageSettings$ = new BehaviorSubject<SmartStatementActionDefinitionJson>(undefined);
  updatedBucketSettings$ = new BehaviorSubject<SmartStatementBucketSetting[]>(undefined);
  updatedCcList$ = new BehaviorSubject<string[]>([]);
  updatedBccList$ = new BehaviorSubject<string[]>([]);
  updatedClientPartnerBuckets$ = new BehaviorSubject<SmartStatementBucket[]>([]);
  updatedClientPartnerNotificationType$ = new BehaviorSubject<EmailNotificationType>(EmailNotificationType.CC);
  updatedAdditionalRecipients$ = new BehaviorSubject<CollectionsAdditionalRecipient[]>([]);

  cadence$: Observable<Cadence> = this.store.select(CommonCollectionsState.getFirstCadence);

  throttlingSettingsV1$ = this.cadence$.pipe(map((cadence) => ({ checked: cadence.is_throttle_enabled, days: cadence.throttle_days })));

  sequence$: Observable<CollectionsSequence> = this.cadence$.pipe(
    switchMap((cadence) => this.store.select(CommonCollectionsState.getSequenceAggregatesByCadenceId(cadence?.id))),
    // Currently there is only a single sequence per cadence, so just select the first sequence.
    map((sequenceAggregates) => (sequenceAggregates && sequenceAggregates.length > 0 ? sequenceAggregates[0].sequence_id : undefined)),
    switchMap((sequenceIdOrUndefined) => this.store.select(CommonCollectionsState.getSequenceBySequenceId(sequenceIdOrUndefined))),
  );

  triggersSettingsV1$ = combineLatest([this.sequence$, this.cadence$]).pipe(
    map(([sequence, cadence]) => ({
      triggers: sequence.triggerSchedule.trigger.triggerRules,
      noTriggerStatementDay: cadence.no_trigger_statement_day,
      noTriggerStatementEnabled: cadence.no_trigger_statement_enabled,
      noTriggerStatementOccurrence: cadence.no_trigger_statement_occurrence,
    })),
  );

  autoApproveSettingsV1$ = this.cadence$.pipe(map((cadence) => cadence.is_automated));

  messageSettingsV1$: Observable<SmartStatementActionDefinitionJson> = this.store.select(CommonCollectionsState.getMessageSettings);

  bucketSettingsV1$: Observable<SmartStatementBucketSetting[]> = this.store.select(CommonCollectionsState.getSmartStatementBucketSettings);
  legacyCcList$: Observable<string[]> = this.bucketSettingsV1$.pipe(
    map(
      (bucketSettings) =>
        bucketSettings?.find((bucket) => bucket.smartStatementBucket === SmartStatementBucket.ZERO_TO_THIRTY)?.ccList || [],
    ),
  );
  legacyBccList$: Observable<string[]> = this.bucketSettingsV1$.pipe(
    map(
      (bucketSettings) =>
        bucketSettings?.find((bucket) => bucket.smartStatementBucket === SmartStatementBucket.ZERO_TO_THIRTY)?.bccList || [],
    ),
  );
  zeroToThirtyBucketSettingsV1$ = this.store.select(CommonCollectionsState.getSmartStatementZeroToThirtyBucketSettings);

  //#endregion

  //#region Collections v2 (existing, new, and adapted logic/data)

  isCollectionsV2Enabled$ = this.launchDarklyService.getFeatureFlag(LaunchDarklyFlag.ENABLE_COLLECTIONS_V2);
  isCollectionsV2Enabled(): boolean {
    return this.store.selectSnapshot(LaunchDarklyState.getFeatureFlag(LaunchDarklyFlag.ENABLE_COLLECTIONS_V2));
  }

  statementRecipientLabel$: Observable<StatementRecipientLabel> = this.store
    .select(CommonCollectionsState.getCollectionsMode)
    .pipe(map((mode) => (mode === CollectionsMode.CONTACT ? StatementRecipientLabel.CONTACT : StatementRecipientLabel.CLIENT)));

  updatedThrottlingSettingsV2$ = new BehaviorSubject<CollectionsThrottleSettings>(undefined);
  throttlingSettingsV2$: Observable<CollectionsThrottleSettings> = getLoadedResults(
    this.store.select(CommonCollectionsState.getCollectionsSetting('throttling')),
  );

  updatedTriggerSettingsV2$ = new BehaviorSubject<CollectionsTriggersSettings>(undefined);
  triggersSettingsV2$: Observable<CollectionsTriggersSettings> = getLoadedResults(
    this.store.select(CommonCollectionsState.getCollectionsSetting('triggers')),
  );

  updatedAutoApproveSettingV2$ = new BehaviorSubject<boolean>(undefined);
  autoApproveSettingV2$: Observable<boolean> = getLoadedResults(
    this.store.select(CommonCollectionsState.getCollectionsSetting('autoApprove')),
  );

  emailSettingsV2$ = getLoadedResults(this.store.select(CommonCollectionsState.getCollectionsSetting('email')));

  updatedBucketSettingsV2$ = new BehaviorSubject<BucketSettings[]>(undefined);
  bucketSettingsV2$ = this.emailSettingsV2$.pipe(map((emailSettings) => emailSettings.bucketSettings));

  zeroToThirtyBucketSettingsV2$: Observable<SmartStatementBucketSetting> = getLoadedResults(
    this.store.select(CommonCollectionsState.getCollectionsSetting('email')),
  ).pipe(
    map(
      (emailSettings) =>
        emailSettings.bucketSettings.find(
          (bucket) => bucket.smartStatementBucket === SmartStatementBucket.ZERO_TO_THIRTY,
        ) as SmartStatementBucketSetting,
    ),
  );

  dynamicFields$: Observable<Map<string, string>> = this.store.select(CommonCollectionsState.getMessageVariables);

  updatedMessageSettingsV2$ = new BehaviorSubject<MessageSettings>(undefined);
  messageSettingsV2$: Observable<MessageSettings> = this.emailSettingsV2$.pipe(
    map((emailSettings) => ({ from: emailSettings.from, fromName: emailSettings.fromName })),
  );

  static combineRecipients(
    existingRecipients: CollectionsAdditionalRecipient[],
    incomingRecipients: string[],
    incomingSourceType: EmailNotificationType,
    smartStatementBucket: SmartStatementBucket,
  ): CollectionsAdditionalRecipient[] {
    if (isEmpty(incomingRecipients)) {
      return existingRecipients;
    }

    const [matchingRecipients, newRecipients] = partition(incomingRecipients, (email) =>
      existingRecipients.some((recipient) => recipient.email === email),
    );

    const updatedExistingRecipients = matchingRecipients.reduce((updatedRecipients, email) => {
      const matchingRecipientIndex = updatedRecipients.findIndex((recipient) => recipient.email === email);
      const matchingRecipient = updatedRecipients[matchingRecipientIndex];
      return replaceArrayItem(updatedRecipients, matchingRecipientIndex, {
        ...matchingRecipient,
        selectedBuckets: matchingRecipient.selectedBuckets.concat(smartStatementBucket),
      });
    }, existingRecipients);

    const updatedIncomingRecipients = newRecipients.map((email) => ({
      email,
      emailNotificationType: incomingSourceType,
      selectedBuckets: [smartStatementBucket],
    }));

    return updatedExistingRecipients.concat(updatedIncomingRecipients);
  }

  //#endregion

  //#region Bridge between v1 and v2

  selectObservableByVersion$<T>(v1: Observable<T>, v2: Observable<T>): Observable<T> {
    return this.isCollectionsV2Enabled$.pipe(switchMap((isEnabled) => (isEnabled ? v2 : v1)));
  }

  throttlingSettings$ = this.selectObservableByVersion$<ThrottlingState>(
    this.throttlingSettingsV1$,
    this.throttlingSettingsV2$.pipe(map((throttling) => ({ checked: throttling.throttleEnabled, days: throttling.throttleDays }))),
  ).pipe(ignoreNullish());

  triggersSettings$ = this.selectObservableByVersion$<
    | (Omit<CollectionsTriggersSettings, 'expressionList'> & { triggers: TriggerRule[] }) // v1
    | CollectionsTriggersSettings // v2
  >(this.triggersSettingsV1$, this.triggersSettingsV2$).pipe(ignoreNullish());

  autoApproveSetting$ = this.selectObservableByVersion$<boolean>(this.autoApproveSettingsV1$, this.autoApproveSettingV2$).pipe(
    ignoreNullish(),
  );

  zeroToThirtyBucketSettings$ = this.selectObservableByVersion$<SmartStatementBucketSetting>(
    this.zeroToThirtyBucketSettingsV1$,
    this.zeroToThirtyBucketSettingsV2$,
  ).pipe(ignoreNullish());

  bucketSettings$ = this.selectObservableByVersion$<BucketSettings[]>(
    this.bucketSettingsV1$ as Observable<BucketSettings[]>,
    this.bucketSettingsV2$,
  ).pipe(ignoreNullish());

  /**
   * Additional recipients are a combination of the ccList and bccList from the bucket settings
   * and are recipient based instead of bucket based.
   */
  additionalRecipients$ = this.bucketSettings$.pipe(
    map((buckets) => {
      const validBuckets = buckets?.filter(Boolean);

      if (isEmpty(validBuckets)) {
        return [];
      }

      return validBuckets.reduce((recipients, bucket) => {
        const recipientsAndCCs = CollectionsSettingsComponent.combineRecipients(
          recipients,
          bucket.ccList,
          EmailNotificationType.CC,
          bucket.smartStatementBucket,
        );

        return CollectionsSettingsComponent.combineRecipients(
          recipientsAndCCs,
          bucket.bccList,
          EmailNotificationType.BCC,
          bucket.smartStatementBucket,
        );
      }, [] as CollectionsAdditionalRecipient[]);
    }),
  );

  clientPartnerBuckets$ = this.bucketSettings$.pipe(
    map((bucketSettings) => bucketSettings.filter((bucket) => !!bucket.clientPartnerEmailNotificationType)),
    map((bucketSettings) => bucketSettings.map((bucket) => bucket.smartStatementBucket)),
  );

  clientPartnerNotificationType$ = this.bucketSettings$.pipe(
    map((bucketSettings) => (bucketSettings ?? []).map((bucket) => bucket.clientPartnerEmailNotificationType).filter(Boolean)),
    map((notificationTypes) =>
      !notificationTypes.length || notificationTypes.some((type) => type === EmailNotificationType.CC)
        ? EmailNotificationType.CC
        : EmailNotificationType.BCC,
    ),
  );

  messageSettings$ = this.selectObservableByVersion$<MessageSettings>(
    this.messageSettingsV1$.pipe(map((messageSettings) => ({ from: messageSettings.from, fromName: messageSettings.fromName }))),
    this.messageSettingsV2$,
  ).pipe(ignoreNullish());

  //#endregion

  //#region Form Validity

  // Individual form valid stated
  isThrottlingFormValid$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  isTriggersFormValid$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  isAutoApproveFormValid$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  isAddressFormValid$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  isMessagesFormValid$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  isBucketMessagesFormValid$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);

  areAllFormsValid$: Observable<boolean> = combineLatest([
    this.isThrottlingFormValid$,
    this.isTriggersFormValid$,
    this.isAutoApproveFormValid$,
    this.isAddressFormValid$,
    this.isMessagesFormValid$,
    this.isBucketMessagesFormValid$,
  ]).pipe(mapToAndResult(), distinctUntilChanged());

  //#endregion

  //#region Check for changes

  doAsyncValuesMatch$<T>(original$: Observable<T>, updated$: Observable<T>, comparator?: (a: T, b: T) => boolean): Observable<boolean> {
    return combineLatest([original$, updated$]).pipe(
      map(([original, updated]) => {
        if (comparator) {
          return comparator(original, updated);
        }

        return typeof original === 'object' ? hashObject(original) === hashObject(updated as object) : original === updated;
      }),
      takeUntil(this._unsubscribe$),
    );
  }

  doCadencesMatch$ = this.doAsyncValuesMatch$(this.cadence$, this.updatedCadence$, CollectionsSettingsComponent.doCadencesMatch);
  doSequencesMatch$ = this.doAsyncValuesMatch$(this.sequence$, this.updatedSequence$, CollectionsSettingsComponent.doSequencesMatch);
  doMessageSettingsMatch$ = this.doAsyncValuesMatch$(
    this.messageSettingsV1$,
    this.updatedMessageSettings$,
    CollectionsSettingsComponent.doMessageSettingsMatch,
  );
  doBucketSettingsMatch$ = this.doAsyncValuesMatch$(
    this.bucketSettingsV1$.pipe(
      map((buckets) => buckets ?? []),
      map((buckets) =>
        buckets.map((bucket) => ({
          ...bucket,
          header: bucket.isHeaderInherited ? null : bucket.header,
          subject: bucket.isSubjectInherited ? null : bucket.subject,
          body: bucket.isBodyInherited ? null : bucket.body,
        })),
      ),
    ),
    this.updatedBucketSettings$.pipe(map((buckets) => buckets ?? [])),
  );
  doCcListsMatch$ = this.doAsyncValuesMatch$(this.legacyCcList$, this.updatedCcList$);
  doBccListsMatch$ = this.doAsyncValuesMatch$(this.legacyBccList$, this.updatedBccList$);
  doClientPartnerBucketsMatch$ = this.doAsyncValuesMatch$(this.clientPartnerBuckets$, this.updatedClientPartnerBuckets$);
  doClientPartnerNotificationTypeMatch$ = this.doAsyncValuesMatch$(
    this.clientPartnerNotificationType$,
    this.updatedClientPartnerNotificationType$,
  );
  doAdditionalRecipientsMatch$ = this.doAsyncValuesMatch$(this.additionalRecipients$, this.updatedAdditionalRecipients$);

  allMatchStates$ = combineLatest([
    this.doCadencesMatch$,
    this.doSequencesMatch$,
    this.doMessageSettingsMatch$,
    this.doBucketSettingsMatch$,
    this.doCcListsMatch$,
    this.doBccListsMatch$,
    this.doClientPartnerBucketsMatch$,
    this.doClientPartnerNotificationTypeMatch$,
    this.doAdditionalRecipientsMatch$,
  ]).pipe(shareReplay(1));

  // If one of the updated models does not match the original provided model, user is allowed to save
  // TODO :: models do not line up 100%, investigate individual forms
  canSaveV1$: Observable<boolean> = combineLatest([this.areAllFormsValid$, this.allMatchStates$]).pipe(
    map(([allFormsValid, [...doMatchChecks]]) => allFormsValid && doMatchChecks.some((check) => !check)),
  );

  canSaveV2$: Observable<boolean> = combineLatest([this.areAllFormsValid$, defer(() => this.updatedCollectionsSettingsPayload$)]).pipe(
    map(([allFormsValid, updatedPayload]) => allFormsValid && !isEmpty(updatedPayload)),
  );

  canSave$: Observable<boolean> = this.selectObservableByVersion$(this.canSaveV1$, this.canSaveV2$);

  /**
   * Compare sequence objects to determine if they are the same
   * @param cadence
   * @param update
   */
  static doCadencesMatch(cadence: Cadence, update: Cadence): boolean {
    if (!cadence && !update) {
      return true;
    } else if (!cadence || !update) {
      return false;
    }

    return (
      cadence.is_automated === update.is_automated &&
      cadence.is_throttle_enabled === update.is_throttle_enabled &&
      cadence.throttle_days === update.throttle_days &&
      cadence.no_trigger_statement_enabled === update.no_trigger_statement_enabled &&
      cadence.no_trigger_statement_occurrence === update.no_trigger_statement_occurrence &&
      cadence.no_trigger_statement_day === update.no_trigger_statement_day
    );
  }

  /**
   * Compare sequence objects to determine if they are the same
   * @param sequence
   * @param update
   */
  static doSequencesMatch(sequence: CollectionsSequence, update: CollectionsSequence): boolean {
    if (!sequence && !update) {
      return true;
    } else if (!sequence || !update) {
      return false;
    }

    const existingTriggerRules = sequence.triggerSchedule.trigger.triggerRules;
    const updatedTriggerRules = update.triggerSchedule.trigger.triggerRules;

    if (existingTriggerRules.length !== updatedTriggerRules.length) {
      return false;
    }

    return (
      CollectionsSettingsComponent.hasAllTriggerRules(existingTriggerRules, updatedTriggerRules) &&
      CollectionsSettingsComponent.hasAllTriggerRules(updatedTriggerRules, existingTriggerRules)
    );
  }

  static hasAllTriggerRules(sourceRules: TriggerRule[], targetRules: TriggerRule[]): boolean {
    return sourceRules.every((sourceRule) => targetRules.some((targetRule) => targetRule.rule.expression === sourceRule.rule.expression));
  }

  /**
   * Compare Message settings objects to determine if they are the same
   * @param settings
   * @param update
   */
  static doMessageSettingsMatch(settings: SmartStatementActionDefinitionJson, update: SmartStatementActionDefinitionJson): boolean {
    if (!settings && !update) {
      return true;
    } else if (!settings || !update) {
      return false;
    }

    return settings.fromName === update.fromName && settings.to === update.to && settings.from === update.from;
  }

  /** v2 */

  existingCollectionsSettingsPayload$: Observable<Omit<CollectionsSettingsV2, 'exclusionList'>> = combineLatest([
    this.throttlingSettingsV2$,
    this.triggersSettingsV2$,
    this.autoApproveSettingV2$,
    this.emailSettingsV2$,
  ]).pipe(
    map(([throttling, triggers, autoApprove, emailSettings]) => ({
      ...throttling,
      ...triggers,
      autoApprove,
      emailSettings,
    })),
    shareReplay(1),
  );

  updatedCollectionsSettingsPayload$ = defer(() => this.pageStatus$).pipe(
    isLoaded(),
    take(1),
    switchMap((_) =>
      combineLatest([
        this.existingCollectionsSettingsPayload$,
        combineLatest([
          this.updatedThrottlingSettingsV2$,
          this.updatedTriggerSettingsV2$,
          this.updatedAutoApproveSettingV2$,
          this.updatedBucketSettingsV2$,
          this.updatedMessageSettingsV2$,
        ]),
      ]),
    ),
    skip(2), // ignore initial value of behavior subjects and second emission caused by updatedBucketSettingsV2$
    map(([existingPayload, [throttling, triggers, autoApprove, buckets, messageSettings]]) => {
      const updatedThrottlingSettings = CollectionsSettingsComponent.getUpdatedFields<CollectionsThrottleSettings>(
        existingPayload,
        throttling,
      );
      const updatedTriggerSettings = CollectionsSettingsComponent.getUpdatedTriggers(existingPayload, triggers);
      const updatedAutoApproveSetting = autoApprove === existingPayload.autoApprove ? {} : { autoApprove };
      const updatedBucketSettings = this.getUpdatedBuckets(existingPayload, buckets);
      const updatedMessageSettings = CollectionsSettingsComponent.getUpdatedFields<MessageSettings>(
        existingPayload.emailSettings,
        messageSettings,
      );

      const updatedEmailSettings: RecursivePartial<CollectionsEmailSettings> = {
        ...updatedMessageSettings,
        ...(isEmpty(updatedBucketSettings) ? {} : { bucketSettings: updatedBucketSettings }),
      };

      const diffPayload: UpdateCollectionsSettingsPayload = {
        ...updatedThrottlingSettings,
        ...updatedTriggerSettings,
        ...updatedAutoApproveSetting,
        ...(isEmpty(updatedEmailSettings) ? {} : { emailSettings: updatedEmailSettings }),
      };

      delete diffPayload.id; // While it is part of the settings v2 interface, the payload should not contain the ID.
      return diffPayload;
    }),
    shareReplay(1),
  );

  static getUpdatedTriggers(
    existingPayload: UpdateCollectionsSettingsPayload,
    triggers: CollectionsTriggersSettings,
  ): RecursivePartial<CollectionsTriggersSettings> {
    const { expressionList, ...noTriggerSettings } = triggers;

    const updatedAndAddedExpressions = expressionList.reduce((updatedExpressions, expression) => {
      const matchingExpression = existingPayload.expressionList.find(({ id }) => id === expression.id);

      // added
      if (!matchingExpression) {
        return updatedExpressions.concat(expression);
      }

      const updatedExpression = CollectionsSettingsComponent.getUpdatedFields(matchingExpression, expression);

      // not updated
      if (isEmpty(updatedExpression)) {
        return updatedExpressions;
      }

      // updated
      return updatedExpressions.concat({ id: expression.id, ...updatedExpression });
    }, [] as RecursivePartial<SmartStatementAgingExpression>[]);

    // removed
    const removedExpressions: Pick<SmartStatementAgingExpression, 'id' | 'active'>[] = existingPayload.expressionList
      .filter((existingExpression) => !expressionList.some(({ id }) => id === existingExpression.id))
      .map(({ id }) => ({ id, active: false }));

    const updatedNoTriggerSettings = CollectionsSettingsComponent.getUpdatedFields(existingPayload, noTriggerSettings);

    const finalExpressionList = updatedAndAddedExpressions.concat(removedExpressions);

    return {
      ...updatedNoTriggerSettings,
      ...// TODO (CV2): Can expression list be made empty?
      (isEmpty(finalExpressionList) && !(isEmpty(expressionList) && !isEmpty(existingPayload.expressionList))
        ? {}
        : { expressionList: finalExpressionList }),
    };
  }

  getUpdatedBuckets(existingPayload: UpdateCollectionsSettingsPayload, buckets: BucketSettings[]): Partial<BucketSettings>[] {
    return buckets.reduce((updatedBuckets, { name, ...bucket }) => {
      // 'name' is not included in payload so pull it from object
      const matchingExistingBucket = existingPayload.emailSettings.bucketSettings.find(
        (existingBucket) => existingBucket.smartStatementBucket === bucket.smartStatementBucket,
      );
      if (!matchingExistingBucket) {
        // bucket has been added by user but is not allowed, so this is just being safe ;)
        return updatedBuckets.concat(bucket);
      }

      const updatedBucket: Partial<BucketSettings> = CollectionsSettingsComponent.getUpdatedFields(matchingExistingBucket, bucket);

      // clientPartnerEmailNotificationType must always be included in the payload so the backend can
      // distinguish "not provided" (default null in DTO) from "explicitly cleared" (user unchecked).
      // The backend uses null-coalescing which would otherwise silently keep the old value.
      const isClientPartnerChanged =
        bucket.clientPartnerEmailNotificationType !== matchingExistingBucket.clientPartnerEmailNotificationType;

      if (isEmpty(updatedBucket) && !isClientPartnerChanged) {
        return updatedBuckets;
      }

      return updatedBuckets.concat({
        ...updatedBucket,
        smartStatementBucket: bucket.smartStatementBucket,
        clientPartnerEmailNotificationType: bucket.clientPartnerEmailNotificationType,
      });
    }, [] as Partial<BucketSettings>[]);
  }

  /**
   * Get the updated fields between two objects. Iterates over arrays and objects recursively.
   * @param oldData
   * @param newData
   * @param ignoreNullish
   * @param includeUpdatedObjectIds If object with an update has an 'id' field, include it.
   */
  static getUpdatedFields<T extends object>(
    oldData: T,
    newData: RecursivePartial<T>,
    ignoreNullish: boolean | ((key: NestedKey<T>) => boolean) = true,
    includeUpdatedObjectIds = true,
  ): RecursivePartial<T> {
    return Object.entries(newData).reduce((acc, [key, newValue]) => {
      /** ignore nullish */
      if (
        newValue == undefined &&
        (ignoreNullish === true || (typeof ignoreNullish === 'function' && ignoreNullish(key as NestedKey<T>)))
      ) {
        return acc;
      }

      const oldValue = oldData[key as keyof T];

      /** iterate over arrays */
      if (Array.isArray(newValue) && Array.isArray(oldValue)) {
        const bothHaveObjects =
          newValue[0] && oldValue[0] && typeof newValue[0] === 'object' && typeof oldValue[0] === 'object' && !Array.isArray(newValue[0]);

        // primitive arrays
        if (!bothHaveObjects) {
          return {
            ...acc,
            ...(areArraysEqual(oldValue, newValue, { ignoreOrder: true }) ? {} : { [key]: newValue }),
          };
        }

        const updatedArray = newValue.reduce((updated, item, index) => {
          // assumes array contains non-array objects (if we want to compare arrays of arrays, we need to add more logic)
          const updatedItem = CollectionsSettingsComponent.getUpdatedFields(oldValue[index], item, ignoreNullish, includeUpdatedObjectIds);

          if (isEmpty(updatedItem)) {
            return updated;
          }

          return updated.concat({
            ...updatedItem,
            ...(includeUpdatedObjectIds ? CollectionsSettingsComponent.getIdObject(oldValue[index]) : {}),
          });
        }, []);

        return {
          ...acc,
          ...(isEmpty(updatedArray) ? {} : { [key]: updatedArray }),
        };
      }

      /** compare objects (go a level deeper) */
      const bothAreObjects =
        newValue && oldValue && typeof newValue === 'object' && typeof oldValue === 'object' && !Array.isArray(newValue);

      if (bothAreObjects) {
        const nestedDiff = CollectionsSettingsComponent.getUpdatedFields(oldValue as T, newValue, ignoreNullish, includeUpdatedObjectIds);
        const isEmptyDiff = isEmpty(nestedDiff);
        return {
          ...acc,
          ...(isEmptyDiff ? {} : { [key]: nestedDiff }),
          ...(isEmptyDiff || !includeUpdatedObjectIds ? {} : CollectionsSettingsComponent.getIdObject(oldData)),
        };
      }

      /** compare primitives */
      const isEqual = newValue === oldValue;
      return {
        ...acc,
        ...(isEqual ? {} : { [key]: newValue }),
        ...(isEqual || !includeUpdatedObjectIds ? {} : CollectionsSettingsComponent.getIdObject(oldData)),
      };
    }, {});
  }

  static getIdObject<T extends object>(data: T): T extends { id: infer U } ? { id: U } : {} {
    return ('id' in data ? { id: data.id } : {}) as any;
  }

  //#endregion

  //#region Value Change Handlers

  /**
   * Update the throttle cadence variables when the throttle state changes
   * @param newState
   */
  onThrottleChange(newState: ThrottlingState): void {
    if (!newState) {
      return;
    }

    this._throttlingTrack$.next(newState);

    if (this.isCollectionsV2Enabled()) {
      this.updatedThrottlingSettingsV2$.next({
        id:
          this.updatedThrottlingSettingsV2$.value?.id ??
          this.store.selectSnapshot(CommonCollectionsState.getCollectionsSetting('throttling', 'data')).id,
        throttleEnabled: newState.checked,
        throttleDays: newState.days,
      });
    } else {
      this.updatedCadence$.next({
        ...this.updatedCadence$.value,
        is_throttle_enabled: newState.checked,
        throttle_days: newState.days,
      });
    }
  }

  onTriggerChange(
    update: { triggerRules: TriggerRule[] } & Pick<
      GraphQl.CadenceType,
      'no_trigger_statement_day' | 'no_trigger_statement_enabled' | 'no_trigger_statement_occurrence'
    >,
  ): void {
    const updateSeq = this.updatedSequence$.value;
    const updateCad = this.updatedCadence$.value;

    // Updating the trigger rules
    updateSeq.triggerSchedule.trigger.triggerRules = update.triggerRules;

    // Updating no trigger defaults
    updateCad.no_trigger_statement_enabled = update.no_trigger_statement_enabled;
    updateCad.no_trigger_statement_day = update.no_trigger_statement_day;
    updateCad.no_trigger_statement_occurrence = +update.no_trigger_statement_occurrence;

    this.updatedSequence$.next(updateSeq);
    this.updatedCadence$.next(updateCad);
  }

  onTriggerChangeV2(updatedTriggerSettings: CollectionsTriggersSettings): void {
    this.updatedTriggerSettingsV2$.next(updatedTriggerSettings);
  }

  /**
   * Update the automated cadence variable on change of the auto approve form
   * @param isEnabled
   */
  onAutoApproveChange(isEnabled: boolean): void {
    if (isEnabled == undefined) {
      return;
    }

    this.trackService.trackWorkflow(CollectionsSettingsStep.AUTO_APPROVE_TOGGLED, {
      enabled: isEnabled,
    });

    if (this.isCollectionsV2Enabled()) {
      this.updatedAutoApproveSettingV2$.next(isEnabled);
    } else {
      this.updatedCadence$.next({ ...this.updatedCadence$.value, is_automated: isEnabled });
    }
  }

  onBucketChange(buckets: (SmartStatementBucketSetting | BucketSettings)[]): void {
    if (this.isCollectionsV2Enabled()) {
      this.updatedBucketSettingsV2$.next(buckets as BucketSettings[]);
      return;
    }

    const marshaledBuckets = (buckets as SmartStatementBucketSetting[]).map((bucket) => ({
      ...bucket,
      isHeaderInherited: !bucket.header,
      isSubjectInherited: !bucket.subject,
      isBodyInherited: !bucket.body,
    }));
    this.updatedBucketSettings$.next(marshaledBuckets);
  }

  onClientPartnerBucketsChange(buckets: SmartStatementBucket[]): void {
    const bucketTokens = [...(buckets ?? []).map((bucket) => bucket.toString())].sort();
    this.trackService.trackWorkflow(CollectionsSettingsStep.CLIENT_PARTNER_BUCKETS_CHANGED, {
      bucketCount: bucketTokens.length,
      buckets: bucketTokens.length > 0 ? `,${bucketTokens.join(',')},` : '',
    });

    if (!this.isCollectionsV2Enabled()) {
      this.updatedClientPartnerBuckets$.next(buckets);
      return;
    }

    if (!this.updatedBucketSettingsV2$.value) {
      return;
    }

    const hasAnyConfigured = this.updatedBucketSettingsV2$.value.some((b) => !!b.clientPartnerEmailNotificationType);
    if (isEmpty(buckets) && !hasAnyConfigured) {
      return;
    }

    const currentNotificationType =
      this.updatedBucketSettingsV2$.value.find((bucket) => !!bucket.clientPartnerEmailNotificationType)
        ?.clientPartnerEmailNotificationType ?? EmailNotificationType.CC;

    const updatedBucketSettings: BucketSettings[] = this.updatedBucketSettingsV2$.value.map((bucket) => ({
      ...bucket,
      clientPartnerEmailNotificationType: buckets.includes(bucket.smartStatementBucket) ? currentNotificationType : null,
    }));

    this.updatedBucketSettingsV2$.next(updatedBucketSettings);
  }

  onClientPartnerNotificationTypeChange(notificationType: EmailNotificationType): void {
    this.trackService.trackWorkflow(CollectionsSettingsStep.CLIENT_PARTNER_NOTIFICATION_TYPE_CHANGED, {
      notificationType: notificationType === EmailNotificationType.BCC ? 'BCC' : 'CC',
    });

    if (!this.isCollectionsV2Enabled()) {
      this.updatedClientPartnerNotificationType$.next(notificationType);
      return;
    }

    if (!this.updatedBucketSettingsV2$.value) {
      return;
    }

    const updatedBucketSettings: BucketSettings[] = this.updatedBucketSettingsV2$.value.map((bucket) => ({
      ...bucket,
      clientPartnerEmailNotificationType: bucket.clientPartnerEmailNotificationType
        ? notificationType
        : bucket.clientPartnerEmailNotificationType,
    }));

    this.updatedBucketSettingsV2$.next(updatedBucketSettings);
  }

  onAdditionalRecipientsChange(additionalRecipients: CollectionsAdditionalRecipient[]): void {
    if (!this.isCollectionsV2Enabled()) {
      this.updatedAdditionalRecipients$.next(additionalRecipients);
      return;
    }

    const updatedBucketSettings = CollectionsSettingsComponent.applyAdditionalRecipients(
      additionalRecipients,
      this.store.selectSnapshot(CommonCollectionsState.getCollectionsSetting('email', 'data')).bucketSettings,
    );
    this.updatedBucketSettingsV2$.next(updatedBucketSettings);
  }

  static applyAdditionalRecipients(
    additionalRecipients: CollectionsAdditionalRecipient[],
    originalBucketSettings: BucketSettings[],
  ): BucketSettings[] {
    return originalBucketSettings.map((bucket) => {
      const recipientsForBucket = additionalRecipients.filter((recipient) =>
        recipient.selectedBuckets.includes(bucket.smartStatementBucket),
      );

      const updatedCcList = recipientsForBucket.reduce(
        (ccList, recipient) =>
          recipient.emailNotificationType === EmailNotificationType.CC && !ccList.includes(recipient.email)
            ? ccList.concat(recipient.email)
            : ccList,
        [],
      );

      const updatedBccList = recipientsForBucket.reduce(
        (bccList, recipient) =>
          recipient.emailNotificationType === EmailNotificationType.BCC && !bccList.includes(recipient.email)
            ? bccList.concat(recipient.email)
            : bccList,
        [],
      );

      return {
        ...bucket,
        ccList: isEmpty(updatedCcList) && isEmpty(bucket.ccList) ? undefined : updatedCcList,
        bccList: isEmpty(updatedBccList) && isEmpty(bucket.bccList) ? undefined : updatedBccList,
      };
    });
  }

  /**
   * Update the From address for the message settings
   * @param newFrom
   */
  onReplyToChange(newFrom: string): void {
    if (this.isCollectionsV2Enabled()) {
      this.updatedMessageSettingsV2$.next({ ...this.updatedMessageSettingsV2$.value, from: newFrom });
    } else {
      this.updatedMessageSettings$.next({ ...this.updatedMessageSettings$.value, from: newFrom });
    }
  }

  /**
   * Update the From address for the message settings
   * @param newFromName
   */
  onFromNameChange(newFromName: string): void {
    if (this.isCollectionsV2Enabled()) {
      this.updatedMessageSettingsV2$.next({ ...this.updatedMessageSettingsV2$.value, fromName: newFromName });
    } else {
      this.updatedMessageSettings$.next({ ...this.updatedMessageSettings$.value, fromName: newFromName });
    }
  }

  //#endregion

  //#region Model Sync

  setUpdatedBackToOriginal$<T>(original$: Observable<T>, updated$: BehaviorSubject<T>): Observable<T> {
    return original$.pipe(
      tap((original) => updated$.next(cloneDeep(original))),
      takeUntil(this._unsubscribe$),
    );
  }

  // v1
  cadenceModelSync$ = this.setUpdatedBackToOriginal$(this.cadence$, this.updatedCadence$);
  sequenceModelSync$ = this.setUpdatedBackToOriginal$(this.sequence$, this.updatedSequence$);
  messageSettingsModelSync$ = this.setUpdatedBackToOriginal$(this.messageSettingsV1$, this.updatedMessageSettings$);
  bucketSettingsModelSync$ = this.setUpdatedBackToOriginal$(this.bucketSettingsV1$, this.updatedBucketSettings$);
  ccListModelSync$ = this.setUpdatedBackToOriginal$(this.legacyCcList$, this.updatedCcList$);
  bccListModelSync$ = this.setUpdatedBackToOriginal$(this.legacyBccList$, this.updatedBccList$);
  clientPartnerBucketsModelSync$ = this.setUpdatedBackToOriginal$(this.clientPartnerBuckets$, this.updatedClientPartnerBuckets$);
  additionalRecipientsModelSync$ = this.setUpdatedBackToOriginal$(this.additionalRecipients$, this.updatedAdditionalRecipients$);
  clientPartnerNotificationTypeModelSync$ = this.setUpdatedBackToOriginal$(
    this.clientPartnerNotificationType$,
    this.updatedClientPartnerNotificationType$,
  );

  // v2
  throttlingModelSync$ = this.setUpdatedBackToOriginal$(this.throttlingSettingsV2$, this.updatedThrottlingSettingsV2$);
  triggersModelSync$ = this.setUpdatedBackToOriginal$(this.triggersSettingsV2$, this.updatedTriggerSettingsV2$);
  autoApproveModelSync$ = this.setUpdatedBackToOriginal$(this.autoApproveSettingV2$, this.updatedAutoApproveSettingV2$);
  bucketSettingsModelSyncV2$ = this.setUpdatedBackToOriginal$(this.bucketSettingsV2$, this.updatedBucketSettingsV2$);
  messageSettingsModelSyncV2$ = this.setUpdatedBackToOriginal$(this.messageSettingsV2$, this.updatedMessageSettingsV2$);

  //#endregion

  //#region Page status

  pageStatus$: Observable<LoadableStatus> = this.isCollectionsV2Enabled$.pipe(
    switchMap((isEnabled) =>
      isEnabled
        ? combineLatest([
            this.store.select(CommonCollectionsState.getCollectionsSetting('throttling', 'status')),
            this.store.select(CommonCollectionsState.getCollectionsSetting('triggers', 'status')),
            this.store.select(CommonCollectionsState.getCollectionsSetting('autoApprove', 'status')),
            this.store.select(CommonCollectionsState.getCollectionsSetting('email', 'status')),
            this.store.select(CommonCollectionsState.getMessageVariablesStatus),
          ])
        : combineLatest([
            this.store.select(CommonCollectionsState.getCadenceStatus),
            this.sequence$.pipe(
              switchMap((sequence) => this.store.select(CommonCollectionsState.getSequenceStatusBySequenceId(sequence?.id))),
            ),
            this.store.select(CommonCollectionsState.getMessageSettingsStatus),
            this.store.select(CommonCollectionsState.getSmartStatementBucketSettingsStatus),
            this.store.select(CommonCollectionsState.getMessageVariablesStatus),
          ]),
    ),
    map((statuses) => LoadableHelper.coalesceStatus(statuses)),
  );

  isUpdateProcessingV1$: Observable<boolean> = combineLatest([
    this.store.select(CommonCollectionsState.getCadenceUpdateRequestStatus),
    this.store.select(CommonCollectionsState.getSequenceUpdateRequestStatus),
    this.store.select(CommonCollectionsState.getMessageSettingsUpdateRequestStatus),
  ]).pipe(map((statuses) => statuses.includes(LoadableStatus.LOADING)));

  isUpdateProcessingV2$: Observable<boolean> = this.store
    .select(CommonCollectionsState.getCollectionsSetting('updateStatus'))
    .pipe(map((status) => status === LoadableStatus.LOADING));

  isUpdateProcessing$: Observable<boolean> = this.selectObservableByVersion$(this.isUpdateProcessingV1$, this.isUpdateProcessingV2$);

  didUpdateFail$: Observable<boolean> = combineLatest([
    this.store.select(CommonCollectionsState.getCadenceUpdateRequestStatus),
    this.store.select(CommonCollectionsState.getSequenceUpdateRequestStatus),
  ]).pipe(
    map(
      ([cadenceRequestStatus, sequenceRequestStatus]) =>
        cadenceRequestStatus === LoadableStatus.ERROR || sequenceRequestStatus === LoadableStatus.ERROR,
    ),
  );

  // When the alert is shown, scroll to it so the user can see
  @ViewChildren('failedSaveAlert') failedSaveAlert: QueryList<ElementRef<HTMLDivElement>>;
  focusOnUpdateFailAlert$: Observable<unknown> = this.didUpdateFail$.pipe(
    ignoreFalsy(),
    switchMap((_) => getFirstOfQueryList(this.failedSaveAlert)),
    tap((alert) => alert.nativeElement.scrollIntoView(true)),
    takeUntil(this._unsubscribe$),
  );

  //#endregion

  //#region Lifecycle Hooks

  constructor(
    private readonly store: Store,
    private readonly snackBar: MatSnackBar,
    private readonly launchDarklyService: LaunchDarklyService,
    private readonly trackService: CollectionsTrackService,
  ) {}

  ngOnInit(): void {
    this.store.dispatch(new GetMessageVariables());

    this.isCollectionsV2Enabled$
      .pipe(
        take(1),
        tap((isEnabled) => (isEnabled ? this.ngOnInitV2() : this.ngOnInitV1())),
      )
      .subscribe();

    this.trackService.trackWorkflow(CollectionsSettingsStep.VIEWED);

    this.trackPendingChangedSections$.subscribe();

    this._throttlingTrack$
      .pipe(
        debounceTime(CollectionsSettingsComponent.THROTTLING_TRACK_DEBOUNCE_MS),
        distinctUntilChanged((a, b) => a.checked === b.checked && a.days === b.days),
        takeUntil(this._unsubscribe$),
      )
      .subscribe((state) =>
        this.trackService.trackWorkflow(CollectionsSettingsStep.THROTTLING_CHANGED, {
          enabled: state.checked,
          days: state.days,
        }),
      );
  }

  /**
   * Snapshot of the currently-pending changed sections, fed by either the v1 match tuple or the v2
   * diff payload. Read by `ngOnDestroy` to emit `SETTINGS_ABANDONED` only when the user navigates
   * away with unsaved work.
   */
  private readonly trackPendingChangedSections$ = this.selectObservableByVersion$<SettingsChangedSection[]>(
    this.allMatchStates$.pipe(map((matches) => CollectionsSettingsComponent.getChangedSectionsFromMatches(matches))),
    defer(() => this.updatedCollectionsSettingsPayload$).pipe(
      map((payload) => CollectionsSettingsComponent.getChangedSectionsFromDiff(payload)),
    ),
  ).pipe(
    tap((sections) => (this._pendingChangedSections = sections)),
    takeUntil(this._unsubscribe$),
  );

  ngOnInitV1(): void {
    // Dispatch action to load the client cadences, get the cadence by Id (sequences are retrieved when the cadence is fetched)
    this.store
      .dispatch(new GetActiveCadences())
      .pipe(
        switchMap((state) => this.store.select(CommonCollectionsState.getCadenceStatus)),
        isLoaded(),
        // Currently only supporting single cadence, once we support multiple this will need to be populated by the user (IE what cadence they are updating for what user)
        switchMap((_) => this.store.select(CommonCollectionsState.getFirstCadence)),
        // Get the cadence and its associated sequences
        switchMap((cadenceId) =>
          this.store.dispatch([new GetSequenceAggregatesByCadenceId(cadenceId?.id), new GetMessageSettings(cadenceId?.id)]),
        ),
        takeUntil(this._unsubscribe$),
      )
      .subscribe();

    this.cadenceModelSync$.subscribe();
    this.sequenceModelSync$.subscribe();
    this.messageSettingsModelSync$.subscribe();
    this.bucketSettingsModelSync$.subscribe();
    this.ccListModelSync$.subscribe();
    this.bccListModelSync$.subscribe();
    this.clientPartnerBucketsModelSync$.subscribe();
    this.clientPartnerNotificationTypeModelSync$.subscribe();
    this.additionalRecipientsModelSync$.subscribe();
  }

  ngOnInitV2(): void {
    this.store.dispatch(new GetCollectionsSettings());

    this.throttlingModelSync$.subscribe();
    this.triggersModelSync$.subscribe();
    this.autoApproveModelSync$.subscribe();
    this.bucketSettingsModelSyncV2$.subscribe();
    this.messageSettingsModelSyncV2$.subscribe();
  }

  ngAfterViewInit() {
    this.focusOnUpdateFailAlert$.subscribe();
  }

  ngOnDestroy() {
    this.trackService.trackWorkflow(CollectionsSettingsStep.ABANDONED, {
      isDirty: this._pendingChangedSections.length > 0,
      changedSections: this._pendingChangedSections,
    });

    this._unsubscribe$.next();
    this._unsubscribe$.complete();
  }

  //#endregion

  //#region Actions (Save/Reset)

  /**
   * Update the settings if they have been updated by the user
   */
  updateSettings(): void {
    this.additionalRecipientsForm.first?.markAsAllAsTouched();
    this.addressesForm.first?.markAllAsTouched();
    this.throttlingForm.first?.markAllAsTouched();
    this.triggerForm.first?.markAllAsTouched();

    // Build the save plan + emit SETTINGS_SAVE_SUBMITTED (intent) before the canSave gate so the
    // funnel records every save attempt — including ones blocked by validation. SUCCEEDED/FAILED
    // downstream carry the outcome.
    this.selectObservableByVersion$(
      this.allMatchStates$.pipe(
        take(1),
        map((matchChecks) => ({
          actions: this.getSaveActions(...matchChecks),
          changedSections: CollectionsSettingsComponent.getChangedSectionsFromMatches(matchChecks),
        })),
      ),
      this.updatedCollectionsSettingsPayload$.pipe(
        take(1),
        map((payload) => ({
          actions: [new UpdateCollectionsSettings(payload), new ClearCollectionsPageData()],
          changedSections: CollectionsSettingsComponent.getChangedSectionsFromDiff(payload),
        })),
      ),
    )
      .pipe(
        take(1),
        tap(({ changedSections }) =>
          this.trackService.trackWorkflow(CollectionsSettingsStep.SAVE_SUBMITTED, { changedSections }),
        ),
        switchMap((plan) =>
          this.selectObservableByVersion$(this.canSaveV1$, this.canSaveV2$).pipe(
            take(1),
            ignoreFalsy(),
            map(() => plan),
          ),
        ),
        switchMap(({ actions, changedSections }) => {
          const startedAt = performance.now();
          return this.store.dispatch(actions).pipe(
            tap({
              next: () => {
                this.trackService.trackWorkflow(CollectionsSettingsStep.SAVE_SUCCEEDED, {
                  changedSections,
                  operationDurationMs: performance.now() - startedAt,
                });
                this.openSnackbar(`Settings updated.`, SnackbarType.SUCCESS);
              },
              error: (err: unknown) => {
                this.trackService.trackWorkflow(CollectionsSettingsStep.SAVE_FAILED, {
                  changedSections,
                  ...extractFailureContext(err),
                  operationDurationMs: performance.now() - startedAt,
                });
                this.openSnackbar(`Settings failed to update. Please try your request again later.`, SnackbarType.ERROR);
              },
            }),
          );
        }),
        takeUntil(this._unsubscribe$),
      )
      .subscribe();
  }

  /**
   * v1 settings diff is exposed only as a tuple of "match" booleans. The cadence record
   * bundles throttling + auto-approve + the no-trigger-statement defaults, so a single
   * cadence mismatch implies all three sections may have changed; we mark them broadly
   * to keep v1 dashboards readable while v1 is phased out.
   */
  static getChangedSectionsFromMatches(matches: boolean[]): SettingsChangedSection[] {
    const [
      doCadencesMatch,
      doSequencesMatch,
      doMessageSettingsMatch,
      doBucketSettingsMatch,
      doCcListsMatch,
      doBccListsMatch,
      doClientPartnerBucketsMatch,
      doClientPartnerNotificationTypeMatch,
      doAdditionalRecipientsMatch,
    ] = matches;
    const sections = new Set<SettingsChangedSection>();
    if (!doCadencesMatch) {
      sections.add('throttling');
      sections.add('autoApprove');
      sections.add('triggers');
    }
    if (!doSequencesMatch) sections.add('triggers');
    if (!doMessageSettingsMatch) sections.add('message');
    if (!doBucketSettingsMatch) sections.add('bucketMessage');
    if (!doCcListsMatch || !doBccListsMatch || !doAdditionalRecipientsMatch) sections.add('additionalRecipients');
    if (!doClientPartnerBucketsMatch || !doClientPartnerNotificationTypeMatch) sections.add('clientPartner');
    return [...sections];
  }

  static getChangedSectionsFromDiff(payload: UpdateCollectionsSettingsPayload): SettingsChangedSection[] {
    const sections = new Set<SettingsChangedSection>();
    if ('throttleEnabled' in payload || 'throttleDays' in payload) sections.add('throttling');
    if (
      'expressionList' in payload ||
      'noTriggerStatementEnabled' in payload ||
      'noTriggerStatementDay' in payload ||
      'noTriggerStatementOccurrence' in payload
    ) {
      sections.add('triggers');
    }
    if ('autoApprove' in payload) sections.add('autoApprove');
    if (payload.emailSettings?.from !== undefined || payload.emailSettings?.fromName !== undefined) {
      sections.add('message');
    }
    const bucketSettings = payload.emailSettings?.bucketSettings ?? [];
    if (bucketSettings.some((bucket) => 'header' in bucket || 'subject' in bucket || 'body' in bucket)) {
      sections.add('bucketMessage');
    }
    if (bucketSettings.some((bucket) => 'ccList' in bucket || 'bccList' in bucket)) {
      sections.add('additionalRecipients');
    }
    if (bucketSettings.some((bucket) => 'clientPartnerEmailNotificationType' in bucket)) {
      sections.add('clientPartner');
    }
    if (payload.exclusionList?.length) sections.add('exclusions');
    return [...sections];
  }

  getSaveActions(doCadencesMatch: boolean, doSequencesMatch: boolean, ...doRestMatch: boolean[]): any[] {
    const actions: any[] = [];

    if (!doCadencesMatch) {
      actions.push(new UpdateCadence(this.updatedCadence$.value));
    }

    if (!doSequencesMatch) {
      actions.push(new UpdateSequence(this.updatedSequence$.value));
    }

    if (doRestMatch.some((check) => !check)) {
      actions.push(new UpdateMessageSettings(this.updatedMessageSettings$.value, this.getUpdatedBucketSettings()));
    }

    return actions;
  }

  /**
   * Extracted helper function to generate updated bucket settings.
   */
  private getUpdatedBucketSettings(): SmartStatementBucketSetting[] {
    return this.updatedBucketSettings$.value.map((bucket) => {
      const bucketRecipients = this.updatedAdditionalRecipients$.value.filter(({ selectedBuckets }) =>
        selectedBuckets.includes(bucket.smartStatementBucket),
      );

      const [ccList, bccList] = partition(
        bucketRecipients,
        ({ emailNotificationType }) => emailNotificationType === EmailNotificationType.CC,
      ).map((recipients) => recipients.map(({ email }) => email));

      const clientPartnerEmailNotificationType = this.updatedClientPartnerBuckets$.value?.includes(bucket.smartStatementBucket)
        ? this.updatedClientPartnerNotificationType$.value
        : null;

      return { ...bucket, ccList, bccList, clientPartnerEmailNotificationType };
    });
  }

  openSnackbar(message: string, type: SnackbarType): void {
    this.snackBar.openFromComponent(SnackbarComponent, { ...DEFAULT_SNACKBAR_CONFIG, data: { message, type } });
  }

  /**
   * Reset the user's overrides
   */
  onRevert(): void {
    this.trackService.trackWorkflow(CollectionsSettingsStep.REVERT_CLICKED);
    /**
     * Fun fact:
     * When using combine latest, there is a limit to the number of latests you are allowed to combine.
     * Had to break this out into two different observables and then recombine them for the notification.
     */
    const additionalRecipientsFormReset$ = combineLatest([
      this.clientPartnerBuckets$,
      this.clientPartnerNotificationType$,
      this.additionalRecipients$,
      this.zeroToThirtyBucketSettings$,
    ]).pipe(
      take(1),
      tap(([clientPartnerBuckets, clientPartnerNotificationType, additionalRecipients, zeroToThirtyBucketSettings]) => {
        this.additionalRecipientsForm.first?.reset(additionalRecipients, clientPartnerNotificationType, clientPartnerBuckets);
        this.messageForm.first?.reset(zeroToThirtyBucketSettings);

        this.updatedClientPartnerBuckets$.next(clientPartnerBuckets);
        this.updatedClientPartnerNotificationType$.next(clientPartnerNotificationType);
        this.updatedAdditionalRecipients$.next(additionalRecipients);
      }),
    );

    const majorityFormReset$ = combineLatest([
      this.throttlingSettings$,
      this.triggersSettings$,
      this.autoApproveSetting$,
      this.messageSettings$,
      this.legacyCcList$,
      this.legacyBccList$,
    ]).pipe(
      take(1),
      tap(([throttlingSettings, triggersSettings, autoApproveSetting, messageSettings, ccList, bccList]) => {
        // Reset the forms
        this.throttlingForm.first?.reset(throttlingSettings);
        this.triggerForm.first?.reset(triggersSettings);
        this.autoApproveForm.first?.reset(autoApproveSetting);
        this.addressesForm.first?.reset(messageSettings, ccList, bccList);
        this.bucketMessageForm.first?.resetBuckets();
      }),
    );

    const updatesReset$ = this.selectObservableByVersion$<unknown>(this.resetV1$, this.resetV2$);

    combineLatest([majorityFormReset$, additionalRecipientsFormReset$, updatesReset$])
      .pipe(
        take(1),
        tap((_) => this.openSnackbar(`Settings have been reverted back to the last saved state.`, SnackbarType.INFO)),
      )
      .subscribe();
  }

  resetV1$ = combineLatest([
    this.cadence$,
    this.sequence$,
    this.messageSettingsV1$,
    this.bucketSettingsV1$,
    this.legacyCcList$,
    this.legacyBccList$,
  ]).pipe(
    take(1),
    tap(([cadence, sequence, messageSettings, buckets, ccList, bccList]) => {
      this.updatedCadence$.next(cloneDeep(cadence));
      this.updatedSequence$.next(cloneDeep(sequence));
      this.updatedMessageSettings$.next(cloneDeep(messageSettings));
      this.updatedBucketSettings$.next(cloneDeep(buckets));
      this.updatedCcList$.next(cloneDeep(ccList));
      this.updatedBccList$.next(cloneDeep(bccList));
    }),
  );

  resetV2$ = combineLatest([
    this.throttlingSettingsV2$,
    this.triggersSettingsV2$,
    this.autoApproveSettingV2$,
    this.bucketSettingsV2$,
    this.messageSettingsV2$,
  ]).pipe(
    take(1),
    tap(([throttling, triggers, autoApprove, buckets, messageSettings]) => {
      this.updatedThrottlingSettingsV2$.next(cloneDeep(throttling));
      this.updatedTriggerSettingsV2$.next(cloneDeep(triggers));
      this.updatedAutoApproveSettingV2$.next(!!autoApprove);
      this.updatedBucketSettingsV2$.next(cloneDeep(buckets));
      this.updatedMessageSettingsV2$.next(cloneDeep(messageSettings));
    }),
  );

  //#endregion
}
